import { useCallback, useMemo, useState } from 'react'
import { App, Button, Space, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { CameraOutlined, CloudUploadOutlined, PlusOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { PageHeader } from '@/shared/ui/PageHeader'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { formatDate, utcTooltip } from '@/shared/lib/format'
import { AuthedImage } from '@/features/attendance/AuthedImage'
import { EmployeeFormModal } from '@/features/attendance/EmployeeFormModal'
import { EmployeePhotoModal } from '@/features/attendance/EmployeePhotoModal'
import { EmployeeSyncTags } from '@/features/attendance/EmployeeSyncTags'
import { localizeOutcome } from '@/features/attendance/outcomeText'
import {
  deleteEmployee,
  employeePhotoUrl,
  fetchEmployeePage,
  syncEmployee,
  type AttendanceEmployee,
} from '@/features/attendance/attendanceApi'
import type { OperationResult } from '@/shared/api/operationResult'

/**
 * Davomat xodimlari reyestri.
 *
 * <h2>⚠️ SAHIFANING ENG MUHIM USTUNI — SINXRON HOLATI</h2>
 * Qolgan hamma narsa (ism, tabel raqami, muddat) bazadagi yozuv. Sinxron
 * ustuni esa BOSHQA savolga javob beradi: <b>bu odam eshikdan o'ta
 * oladimi?</b> Reyestrda xodim bor, lekin qurilmada yo'q — bu butunlay
 * odatiy holat va u hech qanday xato bermaydi. Faqat shu ustun uni
 * ko'rsatadi.
 *
 * Va ustun UCH XIL bo'lishi shart, ikki xil emas:
 *   · yashil — qurilmada bor
 *   · sariq  — navbatda (bu XATO EMAS, hali bo'lmagan ish)
 *   · qizil  — qurilma RAD ETDI, va SABABI bilan
 * Sababning xom holda ko'rsatilishi — `EmployeeSyncTags` dagi asosiy
 * qaror: `lowFaceQuality` operatorga nima qilish kerakligini aytadi,
 * "sinxronlash muvaffaqiyatsiz" esa hech narsa demaydi.
 *
 * <h2>⚠️ FOTO USTUNI — BEZAK EMAS</h2>
 * Bu rasm qurilmadagi yuz kutubxonasiga ketadigan AYNAN o'sha rasm.
 * "Rasm yo'q" degan yozuv — xodim yuz bilan o'ta olmasligining bevosita
 * sababi, ya'ni bu ustun ham holat ustuni.
 *
 * <h2>Nega saralash yo'q</h2>
 * `AttendanceDevicesPage` bilan bir xil sabab: backend saralashni qabul
 * qilmaydi va `sorter` faqat JORIY sahifani tartiblab, "eng muammoli
 * yuqorida" degan yolg'on va'da berardi.
 */
export function AttendanceEmployeesPage() {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()
  const queryClient = useQueryClient()

  const [editing, setEditing] = useState<AttendanceEmployee | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [photoFor, setPhotoFor] = useState<AttendanceEmployee | null>(null)
  const [result, setResult] = useState<OperationResult | null>(null)
  /** Yugurayotgan qator — "yuklanmoqda" FAQAT bosilgan tugmada tursin */
  const [syncing, setSyncing] = useState<string | null>(null)

  const startSync = useCallback(
    (employee: AttendanceEmployee) => {
      setSyncing(employee.id)
      syncEmployee(employee.id)
        .then((outcome) => {
          void queryClient.invalidateQueries({ queryKey: queryKeys.attendance.all })
          // ⚠️ `OK` bo'lmagan HAR QANDAY natija ekranda QOLADI.
          // `PARTIAL` bu yerda ayniqsa ko'p uchraydi: navbatga qo'yildi,
          // lekin qurilma hozir javob bermadi. Uni "muvaffaqiyatli" deb
          // ko'rsatish operatorga xodim allaqachon eshikdan o'ta oladi
          // degan yolg'on ishonch berardi.
          const shown = localizeOutcome(outcome, 'syncEmployee', t)
          if (shown.status === 'OK') message.success(shown.message)
          else setResult(shown)
        })
        .catch((err: unknown) =>
          setResult({ status: 'FAILED', message: translateError(err, t) }),
        )
        .finally(() => setSyncing(null))
    },
    [queryClient, message, t],
  )

  /**
   * Qurilmaga yuborish — TASDIQ BILAN.
   *
   * ⚠️ Bu amal TASHQI qurilmaga chaqiruv qiladi va noto'g'ri parolda
   * Hikvision bizning manzilimizni ~30 daqiqaga QULFLAYDI. Ya'ni
   * tasodifiy bosish faqat "ish bermaydi" emas — u qurilmani BUTUN
   * BO'LIM uchun yarim soatga yopadi. Xavf kodda yozilgan edi, ekranda
   * esa yo'q: menyudagi band boshqalar bilan bir xil ko'rinardi.
   *
   * Matn UMUMIY EMAS: kimni, qayerga va nima xavf borligini aytadi.
   */
  const confirmSync = useCallback(
    (employee: AttendanceEmployee) => {
      modal.confirm({
        title: t('attendance.employee.syncTitle'),
        content: (
          <Space direction="vertical" size={4}>
            <Typography.Text>
              {t('attendance.employee.syncSubject', {
                name: employee.fullName,
                employeeNo: employee.employeeNo,
              })}
            </Typography.Text>
            <Typography.Text type="warning">
              {t('attendance.employee.syncLockWarn')}
            </Typography.Text>
          </Space>
        ),
        okText: t('attendance.employee.sync'),
        cancelText: t('common.cancel'),
        onOk: () => startSync(employee),
      })
    },
    [modal, t, startSync],
  )

  const confirmDelete = useCallback(
    (employee: AttendanceEmployee) => {
      modal.confirm({
        title: t('attendance.employee.deleteTitle'),
        content: (
          <Space direction="vertical" size={4}>
            <Typography.Text>
              {t('attendance.employee.deleteSubject', {
                name: employee.fullName,
                employeeNo: employee.employeeNo,
              })}
            </Typography.Text>
            {/* ⚠️ OQIBAT OCHIQ AYTILADI va u ikki qismli:
                (a) xodim qurilmalardan olib tashlanadi — bu ISHLAYDI,
                    chunki navbatga `DELETE` tushadi
                (b) hodisalar tarixi QOLADI — `event.employee_no` MATN va
                    FK'siz, ya'ni eski yozuvlar o'qilishda davom etadi
                Ikkinchisini aytmaslik operatorni "tarixni tozalash uchun
                xodimni o'chiraman" degan noto'g'ri qarorga olib kelardi. */}
            <Typography.Text type="secondary">
              {t('attendance.employee.deleteWarn')}
            </Typography.Text>
          </Space>
        ),
        okText: t('attendance.delete'),
        cancelText: t('common.cancel'),
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            const outcome = localizeOutcome(await deleteEmployee(employee.id), 'archiveEmployee', t)
            void queryClient.invalidateQueries({ queryKey: queryKeys.attendance.all })
            if (outcome.status === 'OK') message.success(outcome.message)
            else setResult(outcome)
          } catch (err) {
            setResult({ status: 'FAILED', message: translateError(err, t) })
          }
        },
      })
    },
    [modal, message, queryClient, t],
  )

  const columns = useMemo<ColumnsType<AttendanceEmployee>>(
    () => [
      {
        key: 'photo',
        title: t('attendance.employee.photo'),
        width: 80,
        render: (_, row) => (
          // ⚠️ `photoSha256` manzilga KIRADI (BUG-034). Usiz rasm
          //    almashtirilgandan keyin ham jadvalda eskisi qolardi:
          //    manzil o'zgarmagani uchun `AuthedImage` qayta
          //    so'ramasdi, brauzer keshi esa bir soat eski baytlarni
          //    berardi. Tafsilot `employeePhotoUrl` javadoc'ida.
          <AuthedImage
            url={employeePhotoUrl(row.id, row.photoSha256)}
            present={row.hasPhoto}
            width={40}
            height={50}
            alt={row.fullName}
          />
        ),
      },
      {
        key: 'employeeNo',
        title: t('attendance.employee.employeeNo'),
        width: 120,
        render: (_, row) => <Typography.Text code>{row.employeeNo}</Typography.Text>,
      },
      {
        key: 'fullName',
        title: t('attendance.employee.fullName'),
        width: 220,
        render: (_, row) => <NameCell row={row} />,
      },
      {
        key: 'validity',
        title: t('attendance.employee.validity'),
        width: 200,
        render: (_, row) => <ValidityCell from={row.validFrom} to={row.validTo} />,
      },
      {
        // ⚠️ Sinxron ustuni ATAYLAB oxirgi ma'lumot ustuni, lekin
        // amallardan OLDIN: sahifa ochilishining eng ko'p uchraydigan
        // sababi "nega bu odam o'tolmayapti" degan savol, va javob
        // shu yerda.
        key: 'sync',
        title: t('attendance.employee.syncColumn'),
        width: 260,
        render: (_, row) => <EmployeeSyncTags sync={row.sync} />,
      },
      {
        key: 'actions',
        title: '',
        // To'rt amal bitta "uch nuqta" menyusiga yig'ildi (330 → 56):
        // ilgari ustun sinxron ustunini ekrandan surib chiqarardi.
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, row) => (
          // Huquq `RowActions` ichida filtrlanadi — mantiq o'zgarmadi.
          // ⚠️ «Qurilmaga yuborish» ALOHIDA HUQUQ (`attendance.device.sync`),
          // `employee.write` EMAS. Sabab `permissions.ts` da: bu amal
          // TASHQI qurilmaga chaqiruv qiladi va noto'g'ri parolda
          // Hikvision manzilni ~30 daqiqaga qulflaydi — ya'ni uni
          // takrorlab bosish qurilmani o'zimizdan yopib qo'yadi.
          <RowActions
            items={[
              {
                key: 'edit',
                label: t('attendance.edit'),
                onClick: () => {
                  setEditing(row)
                  setFormOpen(true)
                },
                permission: 'attendance.employee.write',
              },
              {
                key: 'photo',
                label: t('attendance.employee.photoAction'),
                icon: <CameraOutlined />,
                onClick: () => setPhotoFor(row),
                permission: 'attendance.employee.write',
              },
              {
                key: 'sync',
                label: t('attendance.employee.sync'),
                icon: <CloudUploadOutlined />,
                onClick: () => confirmSync(row),
                permission: 'attendance.device.sync',
                loading: syncing === row.id,
              },
              {
                key: 'delete',
                label: t('attendance.delete'),
                onClick: () => confirmDelete(row),
                permission: 'attendance.employee.write',
                danger: true,
                divider: true,
              },
            ]}
          />
        ),
      },
    ],
    [t, syncing, confirmSync, confirmDelete],
  )

  return (
    <>
      <PageHeader
        title={t('nav.attendanceEmployees')}
        description={t('pageIntro.attendanceEmployees')}
        extra={
          <Can permission="attendance.employee.write">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('attendance.employee.createTitle')}
            </Button>
          </Can>
        }
      />

      {result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert
            partialNote={false}
            result={result}
            closable
            onClose={() => setResult(null)}
          />
        </div>
      ) : null}

      <DataTable<AttendanceEmployee>
        queryKey={queryKeys.attendance.employees}
        fetchPage={fetchEmployeePage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="attendance-employees"
        /* Amallar ustuni 330 dan 56 ga qisqargach 274 px bo'shadi —
           yig'indi bo'lmagan joyni ushlab turish jadvalni sababsiz
           gorizontal scrollga majburlardi */
        scrollX={1036}
        stickyFirstColumn={false}
        searchable
        searchPlaceholder={t('attendance.employee.searchPlaceholder')}
        emptyTitle={t('attendance.employee.empty')}
        emptyDescription={t('attendance.employee.emptyHint')}
        /*
          ⚠️ 30 soniya — sinxron holati FON JARAYONI tomonidan o'zgaradi,
          panel tomonidan emas. "Qurilmaga yuborish" tugmasi navbatga
          qatorni qo'yadi, natija esa bir necha soniyadan keyin keladi.
          Avtomatik yangilanmasa operator sariq chipni ko'rib turib,
          u allaqachon yashil bo'lganini bilmasdi va tugmani qayta
          bosardi — bu esa qurilmaga keraksiz chaqiruv.
        */
      />

      <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
        {t('attendance.employee.syncSourceHint')}
      </Typography.Paragraph>

      <EmployeeFormModal
        open={formOpen}
        employee={editing}
        onClose={() => {
          setFormOpen(false)
          setEditing(null)
        }}
      />

      <EmployeePhotoModal employee={photoFor} onClose={() => setPhotoFor(null)} />
    </>
  )
}

/**
 * Amal muddati.
 *
 * ⚠️ "CHEKLANMAGAN" YOZUVI BO'SH KATAKDAN AFZAL. Bo'sh katak "ma'lumot
 * yuklanmadi" bo'lib o'qilardi, holbuki bu aniq va eng ko'p uchraydigan
 * fakt: doimiy xodimda muddat qo'yilmaydi.
 *
 * ⚠️ MUDDATI O'TGANI ALOHIDA BELGILANADI. Bu holatda qurilma xodimni
 * O'ZI o'tkazmaydi (`UserInfo.Valid`), ya'ni "faol" bayrog'i yoqiq
 * bo'lsa ham u eshikdan o'tolmaydi. Belgisiz bu ziddiyat ko'rinmasdi va
 * operator sababni sinxron ustunidan qidirardi — u yerda esa hammasi
 * yashil bo'lardi.
 */
function ValidityCell({ from, to }: { from: string | null; to: string | null }) {
  const { t } = useTranslation()

  if (from === null && to === null) {
    return (
      <Tooltip title={t('attendance.employee.validityNoneHint')}>
        <Typography.Text type="secondary">{t('attendance.employee.validityNone')}</Typography.Text>
      </Tooltip>
    )
  }

  const expired = to !== null && new Date(to).getTime() < Date.now()
  const text = `${from ? formatDate(from) : '…'} — ${to ? formatDate(to) : '…'}`

  return (
    <Space size={6}>
      <Tooltip title={[from, to].filter((v): v is string => v !== null).map(utcTooltip).join('\n')}>
        <Typography.Text>{text}</Typography.Text>
      </Tooltip>
      {expired ? (
        <Tooltip title={t('attendance.employee.validityExpiredHint')}>
          <Tag color="red">{t('attendance.employee.validityExpired')}</Tag>
        </Tooltip>
      ) : null}
    </Space>
  )
}

/**
 * Ism + KIRISH HUQUQINING HOLATI.
 *
 * <h2>⚠️ QAYSI NUQSONNI YOPADI (tashqi tester, B6, 2026-08-14)</h2>
 * Ruxsatni o'chirgan operator qatorni ro'yxatda QAYTA KO'RARDI va
 * undan «hech narsa bo'lmadi» degan xulosa chiqarardi. Aslida ikki
 * narsa sodir bo'lgandi va ikkalasi ham ko'rinmasdi:
 *   1. yozuv ARXIVLANGAN (`active = false`) — buni faqat kulrang
 *      «nofaol» yorlig'i aytardi, u esa «vaqtincha o'chirilgan» deb
 *      o'qilardi;
 *   2. qurilmadan olib tashlash NAVBATGA qo'yilgan — bu esa umuman
 *      TOOLTIP ichida yashiringandi.
 *
 * <p>⚠️ Ikkinchisi XAVFSIZLIK masalasi: navbat bajarilgunicha odam
 * eshikdan HALI O'TA OLADI. Server buni rostini aytadi
 * (`OperationResult.PARTIAL`), panel esa uni ro'yxatga
 * chiqarmasdi — ya'ni ogohlantirish faqat o'chirish lahzasida
 * ko'rinib, sahifa yangilangach YO'QOLARDI.
 *
 * <h2>⚠️ NEGA «arxivlangan», «o'chirilgan» EMAS</h2>
 * Yozuv ROSTDAN o'chirilmaydi (`EmployeeService#archive`): davomat
 * tarixi egasiz qolmasligi kerak. «O'chirildi» deb yozish qatorning
 * ro'yxatda turishini tushuntirib bo'lmaydigan qilardi.
 */
function NameCell({ row }: { row: AttendanceEmployee }) {
  const { t } = useTranslation()

  // ⚠️ Manba — `desiredAction`, `state` EMAS. Navbat qatori
  //    `PENDING` dan `FAILED` ga o'tsa ham o'chirish HALI
  //    bajarilmagan bo'ladi, ya'ni `PENDING` ni tekshirish
  //    ogohlantirishni aynan eng yomon holatda (qurilma rad etdi)
  //    yo'qotardi.
  const pendingRemoval = (row.sync ?? []).filter(
    (s) => s.desiredAction === 'DELETE' && s.state !== 'SYNCED',
  )

  return (
    <Space size={6} wrap>
      <Typography.Text strong>{row.fullName}</Typography.Text>
      {row.active ? null : (
        <Tooltip title={t('attendance.employee.archivedHint')}>
          <Tag color="default">{t('attendance.employee.archived')}</Tag>
        </Tooltip>
      )}
      {pendingRemoval.length > 0 ? (
        <Tooltip
          title={t('attendance.employee.pendingRemovalHint', { count: pendingRemoval.length })}
        >
          {/* ⚠️ QIZIL — `EmployeeSyncTags` dagi "navbat sariq"
              qoidasidan ONGLI CHEKINISH. U yerda sariqning sababi
              "hali bo'lmagan ish, o'zi bajariladi". Bu yerda esa
              kutish davomida ODAM ESHIKDAN O'TADI, ya'ni bu xavf
              holati — va u aynan shunday ko'rinishi kerak. */}
          <Tag color="red">{t('attendance.employee.pendingRemoval')}</Tag>
        </Tooltip>
      ) : null}
    </Space>
  )
}
